import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { LoginRequest } from '@medtrack/shared';

import { api, ApiClientError } from '@/lib/api';
import { useSeedSession, useSession, type SessionPayload } from '@/lib/session';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InlineError } from '@/components/ui/error-state';
import { AuthLayout } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { useAuthConfig } from './authConfig';

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const seedSession = useSeedSession();
  const session = useSession();
  const authConfig = useAuthConfig();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const from = (location.state as LocationState | null)?.from ?? '/';

  const mutation = useMutation<SessionPayload, ApiClientError, LoginRequest>({
    mutationFn: (body) => api.post<SessionPayload>('/auth/login', body),
    // No retry. Repeating a failed login burns the server's per-(IP, email)
    // attempt budget and can lock the user out of their own account.
    retry: false,
    onSuccess: (payload) => {
      seedSession(payload);
      navigate(from, { replace: true });
    },
  });

  // Already signed in — someone hit /login with a live cookie.
  if (session.isSuccess && !mutation.isPending) {
    return <Navigate to={from} replace />;
  }

  const fields = mutation.error?.fields;
  // A failed login is deliberately vague about which half was wrong; the server
  // returns one message for both and the UI must not undo that.
  const generalError =
    mutation.error && !mutation.error.isValidation ? mutation.error : null;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (mutation.isPending) return;
    mutation.mutate({ email: email.trim(), password });
  };

  return (
    <AuthLayout
      title="Medication Tracker"
      subtitle="Sign in to continue"
      footer={
        // Hidden when the server takes no new accounts: a "Create one" link
        // that leads to "signup is closed" is a dead end for exactly the
        // person least able to work out why.
        authConfig.data?.signup === 'closed' ? null : (
          <>
            Need an account?{' '}
            <Link
              to="/signup"
              className="font-medium text-accent underline-offset-4 hover:underline"
            >
              Create one
            </Link>
          </>
        )
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {generalError ? <InlineError error={generalError} /> : null}

        <Input
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fields?.email}
          autoComplete="username"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          required
          autoFocus
        />

        <PasswordField
          name="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fields?.password}
          autoComplete="current-password"
          required
        />

        {authConfig.data?.passwordReset ? (
          <div className="-mt-1 text-right">
            <Link
              to="/forgot-password"
              className="inline-flex min-h-11 items-center text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={mutation.isPending}
          className="w-full"
        >
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

export default LoginPage;
